import * as React from "react"
import { cn } from "../../lib/utils"
import { Button, type ButtonProps } from "./button"

interface ConfirmButtonProps extends Omit<ButtonProps, "onClick"> {
  onConfirm: () => void
  confirmLabel?: React.ReactNode
  /** Extra classes while armed, so the waiting-for-confirmation state is unmistakable. */
  armedClassName?: string
}

/**
 * Destructive button that needs two clicks: the first arms it (label changes),
 * the second within a few seconds runs `onConfirm`.
 */
function ConfirmButton({
  onConfirm,
  confirmLabel = "Click to confirm",
  armedClassName,
  className,
  children,
  ...props
}: ConfirmButtonProps) {
  const [armed, setArmed] = React.useState(false)

  React.useEffect(() => {
    if (!armed) return
    const timeout = window.setTimeout(() => setArmed(false), 3000)
    return () => window.clearTimeout(timeout)
  }, [armed])

  return (
    <Button
      variant="danger"
      {...props}
      className={cn(className, armed && armedClassName)}
      onClick={() => {
        if (armed) {
          setArmed(false)
          onConfirm()
        } else {
          setArmed(true)
        }
      }}
    >
      {armed ? confirmLabel : children}
    </Button>
  )
}

export { ConfirmButton }
